import { useSyncExternalStore } from "react";
import { CheckCircle2, Download, RotateCw, X } from "lucide-react";
import {
  dismissUpdate,
  getAutoUpdateSnapshot,
  installUpdateNow,
  startUpdateDownload,
  subscribeToAutoUpdate,
} from "@/shared/autoUpdate";

const BANNER =
  "w-full shrink-0 text-observe-white font-sans text-sm border-b z-[998]";
const INFO = `${BANNER} bg-observe-light border-observe-lighter`;
const READY = `${BANNER} bg-observe-primary border-observe-light`;
const ACTION =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md h-8 px-3 text-xs font-medium shrink-0 cursor-pointer bg-observe-primary text-observe-white shadow hover:bg-observe-primary/90 active:bg-observe-primary/80";
const READY_ACTION = `${ACTION} bg-observe-light hover:bg-observe-light/90 active:bg-observe-light/80`;
const DISMISS =
  "inline-flex items-center justify-center rounded-md h-8 w-8 shrink-0 cursor-pointer border-0 bg-transparent text-observe-white hover:bg-white/20";

const ProgressBar = ({ percent }: { percent: number }) => (
  <div
    className="h-1 w-full bg-observe-lighter"
    role="progressbar"
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={percent}
    data-testid="update-banner-progress"
  >
    <div
      className="h-full bg-observe-white transition-[width] duration-200 ease-out"
      style={{ width: `${percent}%` }}
    />
  </div>
);

/**
 * Desktop update prompt, rendered in the shell chrome so it is visible whichever
 * MFE is mounted. Silent in a browser: window.electronAPI is absent there, so
 * the status never leaves "idle".
 */
export function UpdateBanner() {
  const { status, version, percent, dismissed, autoEnabled } =
    useSyncExternalStore(
      subscribeToAutoUpdate,
      getAutoUpdateSnapshot,
      getAutoUpdateSnapshot,
    );

  if (status === "idle") return null;
  if ((status === "available" || status === "ready") && dismissed) return null;

  if (status === "available" && !autoEnabled) {
    return (
      <div
        data-testid="update-banner"
        data-state="available"
        role="status"
        aria-live="polite"
        className={INFO}
      >
        <div className="flex items-center gap-2 px-3 py-2">
          <Download size={16} aria-hidden="true" className="shrink-0" />
          <span className="flex-1 truncate">
            Update v{version} is available.
          </span>
          <button
            type="button"
            data-testid="update-banner-download"
            onClick={() => void startUpdateDownload()}
            className={ACTION}
          >
            Download
          </button>
          <button
            type="button"
            data-testid="update-banner-dismiss"
            onClick={dismissUpdate}
            aria-label="Dismiss update prompt"
            className={DISMISS}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  if (status === "ready" && !autoEnabled) {
    return (
      <div
        data-testid="update-banner"
        data-state="ready"
        role="status"
        aria-live="polite"
        className={READY}
      >
        <div className="flex items-center gap-2 px-3 py-2">
          <CheckCircle2 size={16} aria-hidden="true" className="shrink-0" />
          <span className="flex-1 truncate">
            Update v{version} downloaded — restart to install.
          </span>
          <button
            type="button"
            data-testid="update-banner-install"
            onClick={() => void installUpdateNow()}
            className={READY_ACTION}
          >
            <RotateCw size={16} aria-hidden="true" />
            Restart now
          </button>
          <button
            type="button"
            data-testid="update-banner-dismiss"
            onClick={dismissUpdate}
            aria-label="Install on next quit"
            className={DISMISS}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  if (status === "ready" || status === "restarting") {
    return (
      <div
        data-testid="update-banner"
        data-state={status === "ready" ? "restarting" : status}
        role="status"
        aria-live="polite"
        className={READY}
      >
        <div className="flex items-center gap-2 px-3 py-2">
          <CheckCircle2 size={16} aria-hidden="true" className="shrink-0" />
          <span className="flex-1 truncate">
            {status === "ready"
              ? `Update v${version} downloaded — restarting to install…`
              : "Restarting to install update…"}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="update-banner"
      data-state={status === "available" ? "downloading" : status}
      role="status"
      aria-live="polite"
      className={INFO}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <Download size={16} aria-hidden="true" className="shrink-0" />
        <span className="flex-1 truncate">Downloading update v{version}…</span>
      </div>
      <ProgressBar percent={percent} />
    </div>
  );
}
